import React, { useCallback, useMemo, useState } from "react";
import {
  Box,
  Card,
  Stack,
  Typography,
  CircularProgress,
  Alert,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useAssessmentFlow } from "../hooks/useAssessmentFlow";
import { QuestionProgress } from "../components/QuestionProgress";
import { QuestionRenderer } from "../components/QuestionRenderer";
import { AssessmentNavigation } from "../components/AssessmentNavigation";
import {
  useOnboardingQuestions,
  useSubmitOnboardingResponses,
  transformAnswersToResponses,
} from "../hooks";
import { ROUTES } from "../../../shared/utils";
import { OnboardingAnswerValue } from "../types";

export const Assessment: React.FC = () => {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionError, setSubmissionError] = useState<string | null>(null);

  const {
    data: questionsData,
    isLoading: questionsLoading,
    error: questionsError,
  } = useOnboardingQuestions();
  const submitResponsesMutation = useSubmitOnboardingResponses();

  const questions = useMemo(
    () => questionsData?.questions || [],
    [questionsData]
  );

  const {
    answers,
    currentAnswer,
    currentIndex,
    currentQuestion,
    goToNext,
    goToPrevious,
    isCurrentAnswerValid,
    progress,
    setAnswer,
  } = useAssessmentFlow({
    questions,
    initialAnswers: {},
  });

  const handleBack = useCallback(() => {
    if (currentIndex === 0) {
      navigate(ROUTES.LOGIN);
      return;
    }
    goToPrevious();
  }, [currentIndex, goToPrevious, navigate]);

  const completeOnboarding = useCallback(async () => {
    setIsSubmitting(true);
    setSubmissionError(null);

    const responses = transformAnswersToResponses(answers, questions);

    try {
      await submitResponsesMutation.mutateAsync({
        answers: responses,
        completed: true,
      });

      navigate(ROUTES.COMPLETE);
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Failed to submit your responses. Don't worry, we'll still complete your onboarding.";

      setSubmissionError(errorMessage);
      console.error("Failed to submit onboarding responses:", error);

      setTimeout(() => navigate(ROUTES.COMPLETE), 3000);
    } finally {
      setIsSubmitting(false);
    }
  }, [answers, questions, submitResponsesMutation, navigate]);

  const handleNext = useCallback(async () => {
    if (!currentQuestion) return;

    const isLastQuestion = currentIndex === questions.length - 1;

    if (isLastQuestion) {
      await completeOnboarding();
    } else {
      goToNext();
    }
  }, [
    currentQuestion,
    currentIndex,
    questions.length,
    completeOnboarding,
    goToNext,
  ]);

  if (questionsLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: "100vh",
          gap: 2,
          width: "100%",
        }}
      >
        <CircularProgress size={40} />
        <Typography variant="body2" color="text.secondary">
          Loading assessment questions...
        </Typography>
      </Box>
    );
  }

  if (questionsError) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: "100vh",
          gap: 2,
          p: 4,
          width: "100%",
        }}
      >
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load assessment questions. Please try again.
        </Alert>
        <Typography variant="body2" color="text.secondary">
          Error:{" "}
          {questionsError instanceof Error
            ? questionsError.message
            : "Unknown error"}
        </Typography>
      </Box>
    );
  }

  if (!currentQuestion) {
    return null;
  }

  const handleAnswerChange = (value: OnboardingAnswerValue) => {
    setAnswer(currentQuestion.id, value);
  };

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        maxWidth: { md: "md", xs: "100%" },
        height: "100%",
        mx: "auto",
      }}
    >
      <Card
        variant="outlined"
        sx={{
          width: "100%",
          height: "100%",
          borderRadius: 4,
          display: "flex",
          flexDirection: "column",
          borderColor: "divider",
          minHeight: 0,
          overflow: "hidden",
          maxHeight: "100vh",
        }}
      >
        <Box
          sx={{
            flexShrink: 0,
            p: { xs: 3, md: 4 },
            pb: 0,
            borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
            bgcolor: "background.paper",
            position: "sticky",
            top: 0,
            zIndex: 1,
          }}
        >
          <QuestionProgress
            currentStep={currentIndex + 1}
            totalSteps={questions.length}
            progress={progress}
          />
        </Box>

        <Box
          sx={{
            flexGrow: 1,
            minHeight: 0,
            overflowY: "auto",
            overflowX: "hidden",
            p: { xs: 3, md: 4 },
            display: "flex",
            flexDirection: "column",
            "&::-webkit-scrollbar": {
              width: "6px",
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor: "transparent",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: (theme) => theme.palette.action.hover,
              borderRadius: "3px",
              "&:hover": {
                backgroundColor: (theme) => theme.palette.action.selected,
              },
            },
          }}
        >
          <Stack spacing={3.5} sx={{ flexGrow: 1, minHeight: "fit-content" }}>
            {submissionError && (
              <Alert severity="error" onClose={() => setSubmissionError(null)}>
                {submissionError}
              </Alert>
            )}

            <Stack spacing={1.5}>
              <Typography variant="h5" component="h2" fontWeight={600}>
                {currentQuestion.title}
              </Typography>
              {currentQuestion.subtitle && (
                <Typography variant="body2" color="text.secondary">
                  {currentQuestion.subtitle}
                </Typography>
              )}
              {currentQuestion.helperText && (
                <Typography variant="body2" color="text.secondary">
                  {currentQuestion.helperText}
                </Typography>
              )}
            </Stack>

            <Box sx={{ flexGrow: 1, minHeight: "fit-content" }}>
              <QuestionRenderer
                question={currentQuestion}
                value={currentAnswer}
                onChange={handleAnswerChange}
              />
            </Box>
          </Stack>
        </Box>

        <Box
          sx={{
            flexShrink: 0,
            px: { xs: 3, md: 4 },
            pt: 2,
            pb: { xs: 2.5, md: 3 },
            borderTop: (theme) => `1px solid ${theme.palette.divider}`,
            bgcolor: "background.paper",
            position: "sticky",
            bottom: 0,
            zIndex: 1,
          }}
        >
          <AssessmentNavigation
            disableNext={!isCurrentAnswerValid || isSubmitting}
            isFinalStep={currentIndex === questions.length - 1}
            finalLabel="Complete"
            onBack={handleBack}
            onNext={handleNext}
          />
        </Box>
      </Card>

      {isSubmitting && (
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: "rgba(0, 0, 0, 0.5)",
            backdropFilter: "blur(4px)",
            zIndex: 1000,
            borderRadius: 4,
          }}
        >
          <Box
            sx={{
              bgcolor: "background.paper",
              borderRadius: 3,
              p: 4,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 2,
              boxShadow: 3,
            }}
          >
            <CircularProgress size={48} />
            <Typography variant="h6" fontWeight={600}>
              Completing your onboarding
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Please wait while we save your responses...
            </Typography>
          </Box>
        </Box>
      )}
    </Box>
  );
};
